import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import EvidenceSnapshotView from "@/components/EvidenceSnapshotView";
import {
    ArrowLeft, AlertCircle, Brain, Plus, Calendar, ShieldCheck, Award, FileText,
} from "lucide-react";
import { EASING, DURATION } from "@/lib/motionSystem";
import { tierMeta } from "@/lib/tiers";
import { cohortLabel } from "@/lib/format";


const SIGNAL_LABELS = {
    academic: "Academic",
    fee_payment: "Fee Payment",
    developer_professional: "Developer / Professional",
    social_creator: "Social / Creator",
    consumption: "Consumption",
    coverage_consistency: "Coverage & Consistency",
};

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" });
    } catch {
        return String(value);
    }
}

function ScoreGauge({ score, tier }) {
    const pct = Math.max(0, Math.min(1, ((score || 300) - 300) / 600));
    const tc = tierMeta(tier);
    const r = 80;
    const arcLen = Math.PI * r;
    return (
        <svg viewBox="0 0 200 115" className="w-full max-w-[260px] mx-auto">
            <defs>
                <filter id="hist-glow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
            </defs>
            <path d="M 10 105 A 80 80 0 0 1 190 105" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" strokeLinecap="round" />
            <path
                d="M 10 105 A 80 80 0 0 1 190 105"
                fill="none"
                stroke={tc.color}
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={arcLen}
                strokeDashoffset={arcLen * (1 - pct)}
                filter="url(#hist-glow)"
            />
            <text x="10" y="112" className="fill-text-secondary" fontSize="9" fontFamily="JetBrains Mono">300</text>
            <text x="175" y="112" className="fill-text-secondary" fontSize="9" fontFamily="JetBrains Mono">900</text>
        </svg>
    );
}

function SignalBar({ signalKey, signalData }) {
    const score = signalData?.score ?? 0;
    const weight = (signalData?.weight ?? 0) * 100;
    const positives = signalData?.top_positives || [];
    const negatives = signalData?.top_negatives || [];

    return (
        <GlassCard className="p-4">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-text-primary text-sm font-semibold">{SIGNAL_LABELS[signalKey] || signalKey}</p>
                    <p className="text-text-secondary text-xs">Weight: {weight.toFixed(0)}%</p>
                </div>
                <p className="font-mono text-xl font-bold text-text-primary">{Math.round(score)}<span className="text-sm text-text-secondary">/100</span></p>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-3">
                <div
                    className="h-full rounded-full transition-all"
                    style={{
                        width: `${Math.max(0, Math.min(100, score))}%`,
                        background: score >= 70 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444",
                    }}
                />
            </div>
            {(positives.length > 0 || negatives.length > 0) && (
                <div className="space-y-1.5 text-xs">
                    {positives.slice(0, 2).map((p, i) => (
                        <p key={`p-${i}`} className="text-green-pioneer">+ {p}</p>
                    ))}
                    {negatives.slice(0, 2).map((n, i) => (
                        <p key={`n-${i}`} className="text-amber-builder">– {n}</p>
                    ))}
                </div>
            )}
        </GlassCard>
    );
}

export default function ScoreHistoryDetail() {
    const { id } = useParams();
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/api/score/${id}`);
                if (cancelled) return;
                setRecord(data);
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.detail || "Couldn't load this score record.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh">
                <p className="text-text-secondary">Loading score...</p>
            </div>
        );
    }

    if (error || !record) {
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh">
                <GlassCard className="p-8 max-w-md text-center">
                    <AlertCircle size={32} className="text-red-starter mx-auto mb-3" />
                    <h2 className="font-heading text-xl font-semibold text-text-primary mb-2">Score not found</h2>
                    <p className="text-text-secondary text-sm mb-4">{error || "This score record could not be loaded."}</p>
                    <Link to="/genz-credit-scoring/history" className="text-blue-primary text-sm font-medium hover:underline">
                        Back to history
                    </Link>
                </GlassCard>
            </div>
        );
    }

    const tier = tierMeta(record.tier);
    const explanation = record.ai_explanation || {};
    const persona = record.persona_match || {};

    return (
        <div className="pt-24 pb-20 px-6 md:px-12 lg:px-24 min-h-dvh" data-testid="score-history-detail">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="max-w-5xl mx-auto"
            >
                <Link
                    to="/genz-credit-scoring/history"
                    className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm mb-6 transition-colors"
                >
                    <ArrowLeft size={14} /> Back to history
                </Link>

                <GlassCard className="p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <p className="text-text-secondary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
                                <Calendar size={12} /> {formatDate(record.created_at)}
                            </p>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-text-primary mb-3">
                                Score snapshot
                            </h1>
                            <div className="flex items-baseline gap-3 mb-3">
                                <span className="font-mono text-5xl sm:text-6xl font-bold" style={{ color: tier.color, textShadow: `0 0 30px ${tier.glow}` }}>
                                    {record.score}
                                </span>
                                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${tier.bg} ${tier.text} ${tier.border} border`}>
                                    {record.tier}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-text-secondary">
                                <span className="inline-flex items-center gap-1">
                                    <ShieldCheck size={12} /> {Math.round((record.confidence || 0) * 100)}% confidence
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <FileText size={12} /> {record.evidence_count ?? record.evidence_snapshot?.length ?? 0} evidence sources
                                </span>
                                {persona.cohort && (
                                    <span className="inline-flex items-center gap-1">
                                        <Award size={12} /> Cohort: {cohortLabel(persona)}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div>
                            <ScoreGauge score={record.score} tier={record.tier} />
                        </div>
                    </div>
                </GlassCard>

                {(explanation.summary || explanation.persona_narrative) && (
                    <GlassCard className="p-6 mb-8">
                        <h3 className="font-heading text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                            <Brain size={16} className="text-blue-primary" /> AI Analysis
                        </h3>
                        {explanation.summary && (
                            <p className="text-text-primary text-sm leading-relaxed mb-3">{explanation.summary}</p>
                        )}
                        {explanation.persona_narrative && (
                            <p className="text-text-secondary text-sm leading-relaxed">{explanation.persona_narrative}</p>
                        )}
                        {Array.isArray(explanation.improvement_suggestions) && explanation.improvement_suggestions.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-white/5">
                                <p className="text-text-primary text-sm font-semibold mb-2">Improvement suggestions</p>
                                <ul className="space-y-1.5">
                                    {explanation.improvement_suggestions.map((tip, idx) => (
                                        <li key={idx} className="text-text-secondary text-sm flex gap-2">
                                            <span className="text-blue-primary shrink-0">→</span>
                                            <span>{typeof tip === "string" ? tip : tip.text || tip.suggestion || JSON.stringify(tip)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </GlassCard>
                )}

                {record.signal_scores && Object.keys(record.signal_scores).length > 0 && (
                    <section className="mb-8">
                        <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">Signal breakdown</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(record.signal_scores).map(([key, val]) => (
                                <SignalBar key={key} signalKey={key} signalData={val} />
                            ))}
                        </div>
                    </section>
                )}

                <section className="mb-8">
                    <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
                        What you submitted
                    </h3>
                    <p className="text-text-secondary text-sm mb-5">
                        Snapshot of every evidence source used to compute this score, captured at the time of generation.
                    </p>
                    <EvidenceSnapshotView
                        evidence={record.evidence_snapshot || []}
                        theme="dark"
                        emptyMessage="This score predates evidence snapshotting. Generate a new score to see full submission data."
                    />
                </section>

                <div className="flex flex-wrap gap-3 justify-center pt-6 border-t border-white/5">
                    <Link
                        to="/genz-credit-scoring/flow"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-primary text-white text-sm font-medium hover:bg-blue-primary/90 transition-all"
                    >
                        <Plus size={14} /> Generate New Score
                    </Link>
                    <Link
                        to={`/genz-credit-scoring/result/${record.id}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-text-primary text-sm font-medium hover:bg-white/5 transition-all"
                    >
                        Open in Dashboard
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}
